// Confirm an un-match.
//
// This one IS irreversible in the way that matters, so it follows the
// destructive-confirmation pattern (CLAUDE.md §1.8.1) properly. Un-matching
// reverses a real receipt, which means the receipt number is burned and reappears
// on the statement as a reversed entry, and a Finance user's request waits on a
// SuperAdmin.
//
// The consequences are stated in the alert rather than buried, because the cost
// here is genuinely higher than a user would guess from the word "un-match".

import React, { useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { zar } from "../Accounts/lineAllocation";

export default function EraUnmatchModal({
  show,
  onHide,
  match,
  isSuperAdmin,
  submitting,
  onConfirm,
}) {
  const [reason, setReason] = useState("");
  const [closeAlert, setCloseAlert] = useState(false);

  const requestClose = () => {
    // Only interrupt when there is typed work to lose. An empty close just
    // closes, per CLAUDE.md §1.8.1.
    if (reason.trim()) {
      setCloseAlert(true);
      return;
    }
    onHide();
  };

  const discardAndClose = () => {
    setCloseAlert(false);
    setReason("");
    onHide();
  };

  const amount = Number(match?.totals?.applied || 0);

  return (
    <Modal show={show} onHide={requestClose} centered size="lg" scrollable>
      <Modal.Header
        closeButton={!closeAlert && !submitting}
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Un-match{match?.receiptNumber ? `: ${match.receiptNumber}` : ""}
        </Modal.Title>
      </Modal.Header>

      {closeAlert && (
        <Alert variant="info" className="mx-0 mb-0 border-0 rounded-0">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span>Discard the reason you typed and close?</span>
            <span className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                aria-label="Discard and close"
                onClick={discardAndClose}
              >
                <i className="fa-solid fa-check" />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                aria-label="Keep editing"
                onClick={() => setCloseAlert(false)}
              >
                <i className="fa-solid fa-xmark" />
              </Button>
            </span>
          </div>
        </Alert>
      )}

      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          This{" "}
          <strong>
            reverses {match?.receiptNumber ? `receipt ${match.receiptNumber}` : "the receipt"}
          </strong>{" "}
          for {zar.format(amount)} and returns the remittance line to your
          outstanding list. The receipt number is not reused: it stays on the
          statement as a <strong>reversed</strong> entry.
        </Alert>

        {match?.invoices?.length > 0 && (
          <p className="small text-muted">
            Affects{" "}
            {match.invoices.map((i) => i.invoiceNumber).filter(Boolean).join(", ")}.
          </p>
        )}

        {!isSuperAdmin && (
          <Alert variant="info" className="mb-3">
            You will not un-match this yourself. A request goes to every
            SuperAdmin in the practice for approval, the same as any other receipt
            reversal. Nothing changes until one of them approves it.
          </Alert>
        )}

        <Form.Group className="mb-0">
          <Form.Label>Reason</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Why this match is being undone"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="danger"
          onClick={() => onConfirm(reason)}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          {submitting ? (
            <Spinner animation="border" size="sm" role="status" />
          ) : (
            <>
              <i className="fa-solid fa-rotate-left me-2" />
              {isSuperAdmin ? "Un-match" : "Request un-match"}
            </>
          )}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={requestClose}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
